import { ArrowLeft, CaretRight, Lightning, Tag } from "@phosphor-icons/react"
import { IconButton } from "~/components/reusable/Button"
import { type FilterFieldType } from "~/utils/filter"
import { Separator } from "../reusable/Separator"

interface QuickFiltersListProps {
  showQuickFilters: boolean
  setShowQuickFilters: (show: boolean) => void
  quickFilterFields: FilterFieldType[]
  nonBooleanQuickFilters: {
    name: string
    value: string
    type: string
    icon?: React.ReactNode
  }[]
  focusedQuickFilterIndex?: number
  onQuickFilterClick: (
    filterType: { name: string; value: string; type: string; icon?: React.ReactNode },
    isNonBoolean: boolean,
  ) => void
  onQuickFiltersRowClick: () => void
}

export function QuickFiltersList({
  showQuickFilters,
  setShowQuickFilters,
  quickFilterFields,
  nonBooleanQuickFilters,
  focusedQuickFilterIndex = -1,
  onQuickFilterClick,
  onQuickFiltersRowClick,
}: QuickFiltersListProps) {
  if (showQuickFilters) {
    return (
      <div className="flex flex-col p-0 w-full min-w-[280px]">
        {/* Quick Filters Header with Back Button */}
        <div className="flex flex-row items-center gap-2 label-base text-fg-subtle px-2 py-1.5">
          <IconButton size="sm" onClick={() => setShowQuickFilters(false)} variant="transparent">
            <ArrowLeft size={16} />
          </IconButton>
          Quick filters
        </div>
        <Separator orientation="horizontal" />

        {/* Quick Filters List */}
        <div className="flex flex-col gap-1 p-1">
          {nonBooleanQuickFilters.map((filterType, index) => {
            const isFocused = focusedQuickFilterIndex === index
            return (
              <div
                key={`non-boolean-${index}`}
                ref={(el) => {
                  if (isFocused && el) {
                    el.scrollIntoView({ block: "nearest" })
                  }
                }}
                onClick={() => {
                  onQuickFilterClick(filterType, true)
                }}
                className={`px-2 py-1.5 hover:bg-base-hover cursor-pointer label-base rounded-[6px] transition-colors flex flex-row items-center justify-between gap-2 ${
                  isFocused ? "bg-base-hover ring-1 ring-border-base" : ""
                }`}
              >
                <div className="flex flex-row items-center gap-2">
                  {filterType.icon ?? <Tag />}
                  {filterType.name}
                </div>
              </div>
            )
          })}
          {quickFilterFields.map((filterType: FilterFieldType, index: number) => {
            const itemIndex = index + nonBooleanQuickFilters.length
            const isFocused = focusedQuickFilterIndex === itemIndex
            return (
              <div
                key={`boolean-${index}`}
                ref={(el) => {
                  if (isFocused && el) {
                    el.scrollIntoView({ block: "nearest" })
                  }
                }}
                onClick={() => {
                  onQuickFilterClick(filterType, false)
                }}
                className={`px-2 py-1.5 hover:bg-base-hover cursor-pointer label-base rounded-[6px] transition-colors flex flex-row items-center justify-between gap-2 ${
                  isFocused ? "bg-base-hover ring-1 ring-border-base" : ""
                }`}
              >
                <div className="flex flex-row items-center gap-2">
                  {filterType.icon ?? <Tag />}
                  {filterType.name}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="p-1">
      {/* Quick Filters Row */}
      <div
        onClick={onQuickFiltersRowClick}
        className="px-2 py-1.5 hover:bg-base-hover cursor-pointer label-base rounded-[6px] transition-colors flex flex-row items-center justify-between gap-2"
      >
        <div className="flex flex-row items-center gap-2">
          <Lightning />
          Quick filters
        </div>
        <CaretRight />
      </div>
    </div>
  )
}
